
/* eslint-disable import/prefer-default-export */
import { getLongFromShortCode } from '@homeaway/google-address-parser';
import * as pages from './PageList';
import * as predicates from './Predicates';
import { getHostDetails, getLocale } from '../../utils/LocaleMap';
import { getAbovCookie } from '../../utils/CookieUtil';
import { fetchAuthUrlWithService } from '../../utils/AuthUrlUtil';
import reasons from '../ListYourPropertyPage/EligibilityCatch/reasons';
import { findByLocale } from '../../utils/PosUtil';
import { collapseQueryParams } from '../../utils/StringUtil';
import { pathUrls } from '../../constants/common';
import {
    ENCRYPTION_QUERY, ENGLISH_ADDRESS_QUERY, GET_NEXT_PAGE_QUERY, retrieveData
} from '../../actions/graphQlSvc';
import log from '../../utils/ClientLogger';
import { getFuzzyLatLng } from '../../utils/LocationUtil';

export const ASYNC_FUNC = 'ASYNC_FUNC';

const REFRESH_PAGES = [
    'lead',
    'missing-details'
];

const postLocationConfigs = [
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isOwner,
            predicates.isMissingAdvertiser
        ],
        pageName: pages.UPDATE_ACCOUNT_PAGE
    },
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isTraveler,
            predicates.isNotMissingDetails
        ],
        pageName: pages.UPDATE_ACCOUNT_PAGE
    },
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isMissingDetails
        ],
        pageName: pages.MISSING_DETAILS_PAGE
    },
    {
        predicate: [
            predicates.isVrbo,
            predicates.isFullyAuthenticated
        ],
        pageName: pages.ESTIMATE_PAGE
    },
    {
        predicate: [
            predicates.isEg,
            predicates.isExternalFlow,
            predicates.isAAPV2
        ],
        pageName: pages.CL_PROPERTY_INFO
    },
    {
        predicate: [
            predicates.isEg,
            predicates.isExternalFlow
        ],
        pageName: pages.EXTERNAL_FLOW_PAGE
    },
    {
        predicate: [
            predicates.isEg
        ],
        pageName: pages.CL_PROPERTY_INFO
    },
    {
        pageName: pages.LEAD_PAGE
    }
];

// predicates is an array of arrays
const generateConfig = (predicateGroups, pageName) => predicateGroups.map(predicateGroup => ({
    predicate: predicateGroup,
    pageName
}));

const predicatesForManualLocation = [
    [predicates.hasNoLatLng],
    [predicates.isAddressInvalid],
    [predicates.hasUnitNumberError],
    [predicates.isInvalidByGeoCoder]
];

const locationToManualLocationConfig = generateConfig(
    predicatesForManualLocation, pages.MANUAL_LOCATION_PAGE
);

const aapLocationToManualLocationConfig = generateConfig(
    predicatesForManualLocation, pages.AAPMANUAL_LOCATION_PAGE
);

const pageStates = [
    {
        name: pages.BED_BATH_PAGE,
        link: ({ features, query, locale }) => {
            if (typeof window === 'object' && features.isConventionalLodgingFlow) {
                // eslint-disable-next-line max-len
                const vrboLocale = getLocale(new URLSearchParams(window.location.search))
                    || getLocale(new URLSearchParams(query))
                    || findByLocale(locale.display).localeVR;
                const destinationUrl = `${window.location.protocol}//${getHostDetails(vrboLocale)}/${vrboLocale}/list/property-details?theme=mvp&utm_medium=referral&utm_source=ULX_MVP`;
                return destinationUrl;
            }
            return 'property-details';
        },
        nextPages: [
            {
                predicate: [
                    predicates.isUserLocationInGoogleMapsExclusionList
                ],
                pageName: pages.MANUAL_LOCATION_PAGE
            },
            {
                pageName: pages.LOCATION_PAGE
            }
        ]
    },
    {
        name: pages.UPDATE_ACCOUNT_PAGE,
        link: 'update-account',
        nextPages: [
            {
                predicate: [
                    predicates.isNotCAS
                ],
                pageName: pages.ESTIMATE_PAGE
            },
            {
                pageName: pages.VALIDATE_AUTH_TICKET
            }
        ]
    },
    {
        name: pages.UPGRADE_AUTH_REDIRECT,
        link: ({ autoAuthUrl }) => autoAuthUrl
    },
    {
        name: pages.LOCATION_PAGE,
        link: 'location',
        nextPages: [
            ...locationToManualLocationConfig,
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.CONFIRM_LOCATION
            },
            ...postLocationConfigs
        ]
    },
    {
        name: pages.MANUAL_LOCATION_PAGE,
        link: 'manual-location',
        nextPages: [
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.CONFIRM_LOCATION
            },
            ...postLocationConfigs
        ]
    },
    {
        name: pages.CONFIRM_LOCATION,
        link: 'confirm-location',
        nextPages: [
            ...postLocationConfigs
        ]
    },
    {
        name: pages.AAPLOCATION_PAGE,
        link: 'add-property/location',
        nextPages: [
            ...aapLocationToManualLocationConfig,
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.AAPCONFIRM_LOCATION
            },
            {
                pageName: pages.AAPCL_PROPERTY_INFO
            }
        ]
    },
    {
        name: pages.AAPMANUAL_LOCATION_PAGE,
        link: 'add-property/manual-location',
        nextPages: [
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.AAPCONFIRM_LOCATION
            },
            {
                pageName: pages.AAPCL_PROPERTY_INFO
            }
        ]
    },
    {
        name: pages.AAPCONFIRM_LOCATION,
        link: 'add-property/confirm-location',
        nextPages: [
            {
                pageName: pages.AAPCL_PROPERTY_INFO
            }
        ]
    },
    {
        name: pages.AAPCL_PROPERTY_INFO,
        link: 'add-property/property-info',
        // no next pages so far, when the join is submitted the handler
        // config will kick in
        nextPages: [
            {
                predicate: [
                    predicates.isEg,
                    predicates.isClRejected
                ],
                pageName: pages.CL_ELIGIBILITY_CHECK
            },
            {
                predicate: [
                    predicates.isEg
                ],
                pageName: pages.CL_CONTRACT_ONBOARDING_AAP
            }
        ]
    },
    {
        name: pages.VALIDATE_AUTH_TICKET,
        link: ({ locale, autoAuthUrl }) => {
            if (typeof window === 'object') {
                // eslint-disable-next-line max-len
                const estimatePageLink = pageStates.find(x => x.name === pages.ESTIMATE_PAGE).url;
                const abovCookie = getAbovCookie();
                const abov = abovCookie ? `?abov=${abovCookie}` : '';
                const destinationUrl = `${window.location.protocol}//${window.location.host}/${locale.display}/list/${estimatePageLink}${abov}`;
                const fullAuthUrl = fetchAuthUrlWithService(autoAuthUrl, destinationUrl);
                return fullAuthUrl;
            }
            // This should never be called server side, but we have to return something.
            return '';
        }
    },
    {
        name: pages.CL_PROPERTY_INFO,
        link: 'property-info',
        nextPages: [
            {
                pageName: pages.LEAD_PAGE
            }
        ]
    },
    {
        name: pages.CL_EMAIL_VERIFICATION,
        link: ({ joinId }) => `email-verification/${joinId}`,
        nextPages: [
            {
                pageName: pages.CL_IS_EMAIL_VERIFIED
            }
        ]
    },
    {
        name: pages.CL_IS_EMAIL_VERIFIED,
        link: 'email/verified',
        nextPages: [
            {
                pageName: pages.CL_CONTRACT_ONBOARDING
            }
        ]
    },
    {
        name: pages.CL_CONTRACT_ONBOARDING,
        link: () => {
            const isTest = /integration|local|test/.test(window.location?.href);
            const baseUrl = isTest
                ? `${window.location.protocol}//apps.expediapartnercentral.com.integration.sb.karmalab.net`
                : `${window.location.protocol}//apps.expediapartnercentral.com`;
            return `${baseUrl}/onboarding/contractSign`;
        }
    },
    {
        name: pages.CL_ELIGIBILITY_CHECK,
        link: ({ eligibilityStatus }) => {
            switch (eligibilityStatus) {
            case reasons.DEFAULT:
            case reasons.LOCATION:
                return 'eligibility/location';
            case reasons.SIZE_LOCATION:
                return 'eligibility/size';
            case reasons.VRBO:
                return 'eligibility/vrbo';
            case reasons.TIMEOUT:
                return 'add-property/join/timeout';
            default:
                return 'property-details';
            }
        },
        nextPages: [
            {
                predicate: [
                    predicates.isEg,
                    predicates.isTimeout
                ],
                pageName: pages.MULTIPROPERTY_ONBOARDING
            },
            {
                predicate: [
                    predicates.isRedirectVrbo
                ],
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.MULTIPROPERTY_ONBOARDING,
        link: () => {
            const isTest = /integration|local|test/.test(window.location?.href);
            const baseUrl = isTest
                ? `${window.location.protocol}//apps.expediapartnercentral.com.integration.sb.karmalab.net`
                : `${window.location.protocol}//apps.expediapartnercentral.com`;
            return `${baseUrl}/lodging/multiproperty/MultiProperty.html`;
        }
    },
    {
        name: pages.LEAD_PAGE,
        link: 'lead',
        nextPages: [
            {
                predicate: [
                    predicates.isEg,
                    predicates.isClRejected
                ],
                pageName: pages.CL_ELIGIBILITY_CHECK
            },
            {
                predicate: [
                    predicates.isEg
                ],
                pageName: pages.CL_EMAIL_VERIFICATION
            },
            {
                pageName: pages.ESTIMATE_PAGE
            }
        ]
    },
    {
        name: pages.LOGIN_INTERSTITIAL,
        link: 'login-interstitial',
        nextPages: [
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isOwner,
                    predicates.isMissingAdvertiser
                ],
                pageName: pages.UPDATE_ACCOUNT_PAGE
            },
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isTraveler,
                    predicates.isNotMissingDetails
                ],
                pageName: pages.UPDATE_ACCOUNT_PAGE
            },
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isMissingDetails
                ],
                pageName: pages.MISSING_DETAILS_PAGE
            },
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isOwner
                ],
                pageName: pages.ESTIMATE_PAGE
            },
            {
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.MISSING_DETAILS_PAGE,
        link: 'missing-details',
        nextPages: [
            {
                pageName: pages.VALIDATE_AUTH_TICKET
            }
        ]
    },
    {
        name: pages.ESTIMATE_PAGE,
        url: 'estimate-results',
        link: ({ locale }) => {
            const { pathname } = window.location;
            const page = pathname.split('/').pop();
            if (REFRESH_PAGES.includes(page)) {
                return `${window.location.protocol}//${window.location.host}/${locale.display}/list/estimate-results`;
            }
            return 'estimate-results';
        },
        nextPages: [
            {
                pageName: pages.UNIFIED_ONBOARDING
            }
        ]
    },
    {
        name: pages.UNIFIED_ONBOARDING,
        link: ({ expediaId, locale }) => {
            const hostDetails = window.location.host;
            return `${window.location.protocol}//${hostDetails}/${locale.display}/supply/onboard/welcome?propertyId=${expediaId}`;
        }
    },
    {
        name: pages.CLASSIFICATION,
        link: 'classification',
        nextPages: [
            {
                predicate: [
                    predicates.isLodgingFlow,
                    predicates.isUserLocationInGoogleMapsExclusionList
                ],
                pageName: pages.MANUAL_LOCATION_PAGE
            },
            {
                predicate: [
                    predicates.isLodgingFlow
                ],
                pageName: pages.LOCATION_PAGE
            },
            {
                predicate: [
                    predicates.isLanguageSelection
                ],
                pageName: pages.LANGUAGE_SELECTION
            },
            {
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.LANGUAGE_SELECTION,
        link: ({ locale }) => {
            // eslint-disable-next-line max-len
            const vrboLocale = getLocale(new URLSearchParams(window.location.search), locale.canonical);
            const destinationUrl = `${window.location.protocol}//${getHostDetails(vrboLocale)}/${vrboLocale}/list/languageSelection?theme=mvp&utm_medium=referral&utm_source=ULX_MVP`;
            return destinationUrl;
        },
        nextPages: [
            {
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.EXTERNAL_FLOW_PAGE,
        link: ASYNC_FUNC,
        asyncLink: async ({
            address,
            latLng,
            pathSource,
            isGeoCoderEnabled,
            addressValidation,
            placeId,
            langId,
            serverAddressValidation,
            isAddressV2,
            geocoderLatLng,
            partialMatch
        }) => {
            const isUnicode = !Object.keys(addressValidation).reduce(
                (acc, x) => acc && addressValidation[x].isLatin,
                true
            );
            const addressToUse = isUnicode
                ? (await retrieveData(ENGLISH_ADDRESS_QUERY, { placeId })).data.fetchEnglishAddress
                : address;
            const isTest = window.location?.href?.includes('integration') || window.location?.href?.includes('local') || window.location?.href?.includes('test');
            const {
                fuzzy,
                latitude,
                longitude
            } = getFuzzyLatLng(
                isGeoCoderEnabled,
                isAddressV2,
                serverAddressValidation,
                latLng,
                geocoderLatLng,
                partialMatch
            );
            const latLngToUse = {
                lat: latitude,
                lng: longitude
            };
            let { country } = addressToUse;
            if (country.length === 2) country = getLongFromShortCode(country);
            const value = JSON.stringify({
                address: {
                    address1: addressToUse.address1,
                    address2: addressToUse.address2,
                    city: addressToUse.city,
                    stateProvince: addressToUse.stateProvince,
                    postalCode: addressToUse.postalCode,
                    country,
                    ...latLngToUse,
                    fuzzy
                },
                localizedAddress: isUnicode ? {
                    ...address,
                    country,
                    ...latLngToUse,
                    fuzzy,
                    languageCode: langId
                } : null
            });
            log('info', pages.EXTERNAL_FLOW_PAGE, {
                ...JSON.parse(value),
                pathSource,
                isAddressV2,
                partialMatch,
                userAction: 'Property completion'
            });
            const result = (await retrieveData(ENCRYPTION_QUERY, { value })).data.encryptValue;
            return `${isTest ? pathUrls[pathSource].test : pathUrls[pathSource].prod}${collapseQueryParams({ data: result })}`;
        }
    },
    {
        name: pages.CL_CONTRACT_ONBOARDING_AAP,
        link: ASYNC_FUNC,
        asyncLink: async ({
            propertyId, isPaperContractUser
        }) => {
            const result = (await retrieveData(GET_NEXT_PAGE_QUERY, {
                propertyId,
                additionalData: {
                    currentStep: 'basicPropertyHotelier',
                    paperOnlyContracts: isPaperContractUser ? 'true' : 'false'
                }
            })).data.getNext;
            if (result) {
                return `${window.location.protocol}//${window.location.host}${result}`;
            }
            return 'add-property/join/timeout';
        }
    }
];

export {
    pageStates
};
